// eslint-disable-next-line no-unused-vars
import React, { memo } from 'react';
import { bool, object, func, string } from 'prop-types';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import { Button, Loading } from '@carbon/react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@emotion/react';
import { Package, Megaphone, Trash } from 'lucide-react';

import Img from 'Containers/Image/Image';
import QuantitySelect from 'Containers/QuantitySelector/QuantitySelect';
import AddToWishlistMenu from 'Containers/Wishlists/AddToWishlistMenu';
import Price from 'Components/Price/Price';
import ProductPriceRequest from 'Containers/Products/ProductPriceRequest';
import Badges from 'Components/Badges/Badges';

import AddToCart from 'Components/AddToCart/AddToCart';
import { stockAlertWishlistId } from 'Contexts/WishlistContext';
// import useNumberUtils from 'Hooks/useNumberUtils';

const StyledProductWishlistItem = styled.div`
  position: relative;
  display: inline-flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 0 20px;
  font-size: 14px;
  width: 100%;

  > .cds--loading-overlay {
    position: absolute;
    z-index: 99;

    svg {
      transform: scale(1.75);
    }
  }

  &:not(:last-of-type) {
    border-bottom: 1px solid ${ props => props.theme.products.colors.border };
  }

  .preview {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 3px;
    min-width: 50px;
    height: 50px;
    width: 50px;
    margin-right: 7px;
    box-sizing: border-box;
    border: 1px solid ${ props => props.theme.colors.shadowGray };
    overflow: hidden;

    svg.image {
      max-width: 70%;
      stroke: #AFAEAE;
    }

    .image {
      display: block;
      margin: 0 auto;
      height: auto;
      width: auto;
      max-height: 100%;
      max-width: 100%;
    }
  }

  .details {
    width: 39%;
    max-width: 230px;
    flex-grow: 1;

    .badges {
      justify-content: flex-start;
      margin-left: 3px;

      .badge {
        min-height: auto;
        font-size: 12px;
      }
    }
  }

  .title, .sku {
    font-size: 0.875rem;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .title {
    a {
      &:hover {
        color: ${ props => props.theme.colors.blue };
      }

      color: ${ props => props.theme.colors.darkGray };
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  }

  .sku {
    font-weight: 600;
    color: ${ props => props.theme.colors.darkGray };

    span {
      color: ${ props => props.theme.colors.gray };
      font-weight: normal;
    }
  }

  .quantitySelector {
    input {
      font-size: 0.875rem;
    }
  }

  .addToCart {
    font-size: 0.875rem !important;
  }

  .priceQty {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: space-between;
    flex-grow: 1;
    margin: 6px 20px 0 10px;
    align-self: flex-start;

    > div > div > div {
      justify-content: flex-end;
    }
  }

  .price {
    font-size: 0.875rem;
    text-align: right;
    margin-bottom: 16px;

    .currentPrice {
      font-size: 0.875rem;
      line-height: 1rem;
      font-weight: 600;
      color: ${ props => props.theme.colors.darkGray };
    }

    .oldPrice {
      font-size: 0.75rem;
      line-height: 1rem;
    }
  }

  .delete {
    width: 40px;
    height: 40px;
    padding: 0;
    min-height: 0;
    min-width: 0;
    border: 1px solid #FEE7E5;
    background-color: #FCF3F1;
    color: ${ props => props.theme.colors.darkGray };
    outline: 0;
    box-shadow: none;
    text-align: center;

    svg {
      margin: 0 auto;
    }
  }

  .actionButtons {
    width: 31.8%;
    flex-grow: 1;
    max-width: 200px;

    .productPriceRequest button { 
      max-width: 200px;
      width: 100%;
      margin-bottom: 10px;
    }

    .stockAlert {
      background-color: transparent;
      border: 1px solid ${ props => props.theme.colors.default };

      .cds--loading {
        position: static;
        margin-right: 12px;
      }

      .cds--loading__stroke {
        stroke: ${ props => props.theme.colors.default };
      }
    }

    .addToCart,
    .stockAlert {
      &:not(:disabled) {
        border: 1px solid ${ props => props.theme.colors.default };
      }

      svg {
        margin-right: 12px;
      }

      height: 40px;
      max-width: 200px;
      width: 100%;
      font-size: 0.875rem !important;
      margin-bottom: 10px;
    }

    .wishlistActions {
      display: flex;
      justify-content: space-between;
      z-index: 3;

      .cds--dropdown__wrapper {
        flex-grow: 1;
      }
    }
  }
`;
export const StyledAddToWishlist = styled(AddToWishlistMenu)`
  margin-right: 5px;
  max-width: 155px;

  .cds--dropdown__wrapper .cds--list-box__field {
    display: flex;
    background-color: ${ props => props.theme.products.colors.addToFavorite } !important;
    color: ${ props => props.theme.products.colors.addToFavoriteText } !important;
  }

  .cds--list-box__label {
    flex-grow: 1;
    text-align: center;
    padding: 0 6px;
  }

  .cds--list-box__menu-icon {
    height: calc(100% - 2px);
    border-left: 1px solid ${ props => props.theme.products.colors.addToFavoriteBorder };
    background-color: ${ props => props.theme.colors.white };
    min-width: 35px;
    width: 35px;
    right: 1px;
    position: relative;
    display: inline-flex;
  }

  .cds--list-box__menu {
    background-color: ${ props => props.theme.colors.white };
    right: 0;
    left: auto;
    width: auto;
    max-width: 300px;

    .cds--list-box__menu-item__option {
      margin: 0 auto;
      padding-left: 1rem;
      padding-right: 1rem;
    }
  }
`;

const ProductWishlistItem = memo((
  {
    product,
    loading,
    wishlist,
    addToCartLoading,
    // wishlists,
    onStockAlertModalOpen,
    onMoveToWishlist,
    onAddToCart,
    onDelete,
    onChange,
    defaultCurrency,
    withVat
  }
) => {
  // const { formatNumber } = useNumberUtils();
  const { t } = useTranslation();
  const theme = useTheme();
  return (
    <StyledProductWishlistItem theme={ theme }>
      { loading && <Loading active={ loading } small withOverlay /> }
      <Link
        className="preview"
        to={ `/products/${ product.slug }/pd/${ product.id }` }
        data-test="wishlistDropdownLink"
        data-testid={ product.id }
        state={ {
          component: 'product_wishlist_item',
          source: 'mini_wishlist',
          clickedOn: 'image',
          productId: product.id
        } }
      >
        <Img
          className="image"
          src={ product.picture }
          unloader={ <Package size={ 50 } strokeWidth={ 1.25 } /> }
          loading="lazy"
          loader={ <Loading withOverlay={ false } small /> }
          alt={ product.name }
        />
      </Link>
      <div className="details">
        <h4 className="title">
          <Link
            to={ `/products/${ product.slug }/pd/${ product.id }` }
            data-test="wishlistDropdownLink"
            data-testid={ product.id }
            state={ {
              component: 'product_wishlist_item',
              source: 'mini_wishlist',
              clickedOn: 'title',
              productId: product.id
            } }
          >
            { product.name }
          </Link>
        </h4>
        <p className="sku">{ t('product.code') }: <span>{ product.code }</span></p>
        <Badges
          isSupplierOffer={ !product?.hasPrice }
          hasSupplierOffers={ product.hasSupplierOffers }
        />
      </div>
      <div className="priceQty">
        { (!product?.hasPriceRequest && product.priceAvailable && !product?.warnings?.blocked_prices) && (
          <Price
            price={ product.price }
            promoPrice={ product.promoPrice }
            currency={ defaultCurrency }
            withVat={ withVat }
            displayVatInfo
            // withTooltip
            // tooltipContent={ `
            //   ${ t('products.greenStamp') }
            //   ${ formatNumber(product.greenStampValue, { currency: product.currency }) }
            // ` }
          />
        ) }
        { (product.stockAvailable && !(
          product.warnings?.blocked_prices
          || product.warnings?.blocked_stocks
          || product.warnings?.blocked_orders
        )) && (
          <QuantitySelect
            value={ product.quantity }
            min={ Math.max(product.minQuantity, 1) }
            step={ Math.max(product.minQuantity, 1) }
            max={ 100 }
            onChange={ onChange }
            data-test="wishlistDropdownChangeQty"
            data-testid={ product.id }
          />
        ) }
      </div>
      <div className="actionButtons">
        { product?.hasPriceRequest && <ProductPriceRequest productId={ product.id } /> }
        { (
          (!product.stockAvailable || !product.stockValue)
          && !Object.keys(product?.warnings || {}).length > 0
          && wishlist.id !== stockAlertWishlistId
          && !product?.hasPriceRequest
        ) && (
          <Button
            data-test="onStockAlert"
            data-testid={ product.productId }
            className="red stockAlert"
            kind="ghost"
            onClick={ onStockAlertModalOpen }
          >
            <Megaphone />
            { t('products.moveToStockAlert') }
          </Button>
        ) }
        { ((
          (product.stockValue > 0 && wishlist.id !== stockAlertWishlistId)
          || Object.keys(product?.warnings || {}).length > 0)
          && !product?.hasPriceRequest
        ) && (
          <AddToCart
            className="addToCart"
            showSelector={ false }
            disabled={ !!(
              !product.priceAvailable
              || addToCartLoading
              || product.warnings?.blocked_prices
              || product.warnings?.blocked_stocks
              || product.warnings?.blocked_orders
            ) }
            loading={ addToCartLoading }
            onClick={ onAddToCart }
            trackingContext={ {
              location: 'mini_wishlist',
              product_sku: product?.code,
            } }
            data-test="wishlistDropdownAddToCart"
            data-testid={ product.id }
          />
        ) }
        <div className="wishlistActions">
          <StyledAddToWishlist
            action="move"
            wishlist={ wishlist }
            data-test="wishlistItemMove"
            data-testid={ product.id }
            onAddToWishlist={ onMoveToWishlist }
            product={ product }
            label={ t('wishlists.moveToList') }
          />
          <Button
            className="delete"
            kind="ghost"
            renderIcon={ Trash }
            iconDescription={ t('common.delete') }
            onClick={ onDelete }
            data-test="wishlistItemDelete"
            data-testid={ product.id }
          />
        </div>
      </div>
    </StyledProductWishlistItem>
  );
});

ProductWishlistItem.propTypes = {
  // wishlists: array,
  onStockAlertModalOpen: func,
  loading: bool,
  wishlist: object,
  addToCartLoading: bool,
  onMoveToWishlist: func,
  product: object,
  onChange: func,
  onAddToCart: func,
  onDelete: func,
  defaultCurrency: string,
  withVat: bool,
};

ProductWishlistItem.displayName = 'ProductWishlistItemComponent';

export default ProductWishlistItem;
